import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@Components/shadcn/ui/dropdown-menu";
import { LogOut, Users } from "lucide-react";
import {
  LPLogoWName,
  dmSent,
  Automations,
  SupportInactive,
  Check,
  ContactsInactive,
  AddIconSVG,
} from '@assets/index';
import styles from './styles';
import { ChevronDown } from "lucide-react";
import { RootState, useAppDispatch, useAppSelector } from '@Store/store';
import { baseApi } from '@Services/api';
import { AnalyticsParamBuilder, getAccountSwitchRedirectPath, onLogout, refreshAfterAccountSwitch } from '@Utils/common';
import { displayToast } from '@Utils/toast';
import posthog from 'posthog-js';
import { ROUTES } from '@Constants/routes';
import { setSelectedIgAccount } from '@Store/igSelectionSlice'
import { PlusCircleIcon } from '@heroicons/react/24/outline';



interface NavigationItems {
  to: string;
  label: string;
  activeIcon: string;
  inactiveIcon: string;
  end?: boolean;
  isNew?: boolean;
}

interface NavigationItemsProps {
  navigationItems: NavigationItems[];
  setIsMobileMenuOpen: any;
  setUpgradeToPro: any;
}




// Reusable ProgressBar component
interface ProgressBarProps {
  label: string;           // e.g. "DM per month"
  value?: number;          // current count (undefined means unlimited)
  max?: number;            // max value, default 1000
  isUnlimited?: boolean;   // flag for unlimited mode
  iconSrc: string;
  iconAlt: string;
  iconWidth?: number;
}


const getGradient = (percent: number) => {
  if (percent > 70) {
    return 'linear-gradient(90deg, #FF9E9E 0%, #FF6B6B 50%, #D90429 100%)'; // Red
  } else if (false) {
    return 'linear-gradient(90deg, #FFF59D 0%, #FFE082 50%, #FFCA28 100%)'; // Yellow
  } else {
    return 'linear-gradient(90deg, #A5A1FF 0%, #A595DE 50%, #5A3DB4 100%)'; // Blue/Purple
  }
};

const getTextColor = (percent: number) => {
  if (percent > 75) return '#ff0000';        // White on red
  if (false) return '#5d4037';     // Dark brown on yellow
  return '#000000ff';                       // Default dark gray
};


const COMPONENT = "navigation_drawer"



const NavigationDrawer: React.FC<NavigationItemsProps> = ({ navigationItems, setIsMobileMenuOpen, setUpgradeToPro }) => {
  const { user } = useAppSelector((state: RootState) => state.profile);
  const { selectedIgId, selectedIgData } = useAppSelector((state: RootState) => state.igSelection);
  const igAccounts = user?.ig_ids || [];

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch()

  // Search state for accounts
  const [accountSearch, setAccountSearch] = useState("");
  const filteredAccounts = (user?.ig_ids || []).filter((ig: any) => (
    ig?.username?.toLowerCase().includes(accountSearch.toLowerCase())
  ));
  const visibleAccounts = filteredAccounts.slice(0, 5);

  const handleLogout = () => {
    // Add your logout logic here
    // For example: clear local storage, reset auth state, etc.
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "logout",
    }));
    onLogout(dispatch, true)
    navigate('/login');
  };

  // Get the currently active IG account (selected only; do not auto-select first)
  const activeIgAccount = selectedIgData;
  const ig_user = activeIgAccount;



  const ShowPaymentModal = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "nav_pro_icon",
      classValue: user?.is_pro,
    }));

    if (user?.status == "pending") {
      displayToast("error", "Your Subscription Payment is Pending! Contact Support ASAP");
      return
    }
    if (user?.is_pro) return;

    setUpgradeToPro(true);
  }


  const OnNavigationItemClick = (nav_item: NavigationItems) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "nav_item_click",
      classValue: nav_item?.to,
      str_value: nav_item?.label,
    }));

    setIsMobileMenuOpen(false)
  }


  const OnSupportButtonClick = () => {
    const url = 'https://wa.link/9jjwc0'
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "support_button",
      classValue: user?.is_pro,
      str_value: url
    }));
    window.open(url, '_blank')
  }

  const OnAccountsClick = (open: boolean) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "workspace_click",
      classValue: "workspace",
      genus: open || false,
      str_value: `@${user?.ig_ids[0]?.username}`,
    }));
  }

  const onSelectAccount = (ig: any) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "account_switch",
      str_value: ig?.username,
    }));
    dispatch(setSelectedIgAccount({ igId: ig?.igsid, igData: ig }));
    dispatch(baseApi.util.resetApiState());
    setIsMobileMenuOpen(false);
    refreshAfterAccountSwitch(
      getAccountSwitchRedirectPath(
        location.pathname,
        location.search,
        location.hash
      )
    );
  }



  return (
    <aside style={styles.container}>
      {/* Logo Section */}
      <div style={styles.logoSection}>
        <img src={LPLogoWName} alt="LinkPlease Logo" style={styles.logoImage} />
      </div>

      {/* Workspace Selector with Dropdown */}
      <div className="border rounded-[10px] my-2">
        <DropdownMenu onOpenChange={(open) => OnAccountsClick(open)}>
          <DropdownMenuTrigger className="w-full focus:outline-none">
            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <img
                src={activeIgAccount?.profile_picture_url || LPLogoWName}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900">
                  {activeIgAccount ? `@${activeIgAccount?.username}` : 'Select an account'}
                </p>
                {/* <p className="text-xs text-gray-500">
                  {activeIgAccount ? 'Selected Account' : 'No account selected'}
                  {user?.ig_ids && user.ig_ids.length > 1 && (
                    <span className="ml-1">({user.ig_ids.length} accounts)</span>
                  )}
                </p> */}
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[220px] w-[280px]">
            {/* <DropdownMenuLabel>My Account</DropdownMenuLabel> */}
            {/* <DropdownMenuSeparator /> */}
            {/* Instagram Accounts List */}
            {user?.ig_ids?.length ? (
              <>
                {/* <DropdownMenuLabel>Instagram Accounts</DropdownMenuLabel>
                <DropdownMenuSeparator /> */}
                <div className="px-2 py-1">
                  <input
                    type="text"
                    placeholder="Search username"
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    className="w-full border rounded-md px-2 py-1 text-sm"
                  />
                  {filteredAccounts.length > 5 && (
                    <p className="text-xs text-gray-500 mt-1">Showing first 5 results</p>
                  )}
                </div>

                {/* Show selected account first with tinted background */}
                {activeIgAccount && (
                  <DropdownMenuItem
                    key={`selected-${activeIgAccount?.igsid}`}
                    className="cursor-pointer flex items-center gap-2 bg-blue-50 border-l-2 border-blue-500"
                    onClick={() => onSelectAccount(activeIgAccount)}
                  >
                    <img
                      src={activeIgAccount?.profile_picture_url}
                      alt={`@${activeIgAccount?.username}`}
                      className="h-6 w-6 rounded-full object-cover"
                    />
                    <span>@{activeIgAccount?.username}</span>
                    <span className="ml-auto text-xs text-blue-600 font-medium">Current</span>
                  </DropdownMenuItem>
                )}
                {/* Show other accounts (excluding the selected one) */}
                {visibleAccounts
                  .filter((ig: any) => ig?.igsid !== activeIgAccount?.igsid)
                  .map((ig: any) => (
                    <DropdownMenuItem
                      key={ig?.igsid}
                      className="cursor-pointer flex items-center gap-2"
                      onClick={() => onSelectAccount(ig)}
                    >
                      <img
                        src={ig?.profile_picture_url}
                        alt={`@${ig?.username}`}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                      <span>@{ig?.username}</span>
                    </DropdownMenuItem>
                  ))}
                {/* Primary action: Add Instagram (pinned near top for scale) */}
                <DropdownMenuItem
                  className="cursor-pointer py-4 flex justify-center"
                  disabled={user?.is_enterprise && parseInt(user?.ent_acc_limit) <= igAccounts.length}
                  onClick={() => { navigate(ROUTES.IG_CONNECT); setIsMobileMenuOpen(false); }}
                >
                  <p className='flex items-center gap-2 text-indigo-600 font-medium'>
                    <PlusCircleIcon className="h-8 w-8" />
                    Add Instagram
                  </p>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer text-center flex justify-center"
                  onClick={() => {
                    navigate(ROUTES.IG_SELECT, {
                      state: {
                        from: getAccountSwitchRedirectPath(
                          location.pathname,
                          location.search,
                          location.hash
                        ),
                      },
                    });
                    setIsMobileMenuOpen(false);
                  }}
                >
                  View all accounts
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuItem
              className="text-red-600 cursor-pointer  flex justify-center"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Navigation Links */}
      <nav style={styles.navigation}>
        {navigationItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => OnNavigationItemClick(item)}
            style={({ isActive }) => styles.navLink(isActive)}
          >
            {({ isActive }) => (
              <>
                <img
                  src={isActive ? item.activeIcon : item.inactiveIcon}
                  alt={item.label}
                  style={styles.navIcon}
                />
                {item.label}
                {item?.isNew && (
                  <span style={{
                    position: 'absolute',
                    right: 25,
                    backgroundColor: 'gold',
                    color: 'black',
                    fontSize: 10,
                    padding: '2px 4px',
                    borderRadius: 6,
                    fontWeight: 'bold'
                  }}>
                    NEW
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Usage Metrics */}
      <div style={styles.metricsContainer}>
        <ProgressBar
          label="DM per month"
          value={ig_user?.automation_triggered}
          isUnlimited={user?.is_pro}
          iconSrc={dmSent}
          iconAlt="DM Sent Icon"
        />
        <ProgressBar
          label="contacts per month"
          value={ig_user?.contacts}
          isUnlimited={user?.is_pro}
          iconSrc={ContactsInactive}
          iconAlt="Contacts Icon"
          iconWidth={25}
        />
      </div>

      {/* Plan Button */}

      <button className='shimmer2' style={styles.shimmerButton} onClick={ShowPaymentModal}>
        {/* <img src="https://img.icons8.com/ios-glyphs/30/star--v1.png" className='w-[20px]'/> */}
        {/* <button className="mr-3 ultimate-gradient flex-shrink-0 px-4 py-1 rounded-xl"> */}
        <img alt="" className="h-4 w-4" src="https://in.imitari.app/zorcha/premium-icon-crown.svg?tr=f-webp,w-600" />
        {/* </button> */}
        {user?.is_pro ? "Pro Plan" : "Upgrade to Pro"}
      </button>



      {/* Bottom Actions */}
      <div style={styles.bottomActions}>
        {/* <button 
          style={styles.secondaryButton}
          className={'hover:bg-[#E0D9FF] hover:text-[#ffffff]'}
        >
          <img src={SettingsInactive} alt="Settings" style={styles.buttonIcon} />
          <span  style={styles.buttonText}>Settings</span>
        </button> */}
        <button
          style={styles.secondaryButton}
          className={'hover:bg-[#E0D9FF] hover:text-[#ffffff]'}
          onClick={OnSupportButtonClick}
        >
          <img className='border rounded-full border-black' src={SupportInactive} alt="Settings" style={styles.buttonIcon} />
          <span style={styles.buttonText}>Support/Feedback</span>
        </button>
      </div>
    </aside>
  );
};

export default NavigationDrawer;

const ProgressBar: React.FC<ProgressBarProps> = ({
  label,
  value,
  max = 1000,
  isUnlimited = false,
  iconSrc,
  iconAlt,
  iconWidth = 30,
}) => {
  const percent = isUnlimited ? 100 : Math.min(((value ?? 0) / max) * 100, 100);

  const displayLabel = isUnlimited
    ? `Unlimited ${label}`
    : `${value ?? 0}/${max} ${label}`;

  // Green gradient for unlimited
  const unlimitedGradient = 'linear-gradient(90deg, #B9BBFF 0%, #7878ff 35%, #3535d5 65%, #151590 100%)';

  return (
    <div style={styles.metricItem}>
      <div style={styles.metricHeader}>
        <img src={iconSrc} alt={iconAlt} style={styles.metricIcon} width={iconWidth} />
        <p
          style={{
            ...styles.metricText,
            color: isUnlimited ? '#000000' : getTextColor(percent),
          }}
        >
          {displayLabel}
        </p>
      </div>
      <div style={styles.progressBar}>
        <div
          style={{
            ...styles.progressFill,
            width: `${percent}%`,
            background: isUnlimited ? unlimitedGradient : getGradient(percent),
          }}
        />
      </div>
    </div>
  );
};
